import PropTypes from "prop-types";
import React, { useRef, useState } from "react";

import { useResizeObserver } from "@swa-ui/browser";
import { Background, getLinearGradient, ListMatrix } from "@swa-ui/core";

import { LayoutSuspense } from "../LayoutSuspense";
import { PlacementFactory } from "../PlacementFactory";
import { PlacementHeading, placementHeadingPropTypes } from "../PlacementHeading";
import styles from "./ContentSection.module.scss";

export const ContentSection = (props) => {
  // TODO: enableContentOrderOverride is temporary to avoid breaking changes to all SPAs, will be removed in QUAMON-1198
  const { additionalProps, componentRenderers, placement, settings } = props;
  const { background, id } = placement ?? {};
  const { enableContentOrderOverride = false } = placement ?? {};
  const { center = true, horizontalSeparator, numberOfColumns, separator } = settings ?? {};
  const childRef = useRef(null);
  const [childHeight, setChildHeight] = useState(0);
  const items = enableContentOrderOverride ? placement?.contentItems?.items : placement?.items;

  useResizeObserver({ callback: updateHeight, element: childRef });

  return (
    <div className={getContainerStyle()} style={getContentSectionStyle()}>
      {background && <Background {...getBackgroundProps()} />}
      <div ref={childRef} style={{ position: "relative" }}>
        <LayoutSuspense>
          {placement?.heading && <PlacementHeading {...placement.heading} />}
          <ListMatrix {...getListMatrixProps()} />
        </LayoutSuspense>
      </div>
    </div>
  );

  function renderItem(item) {
    const { content: inlineContent, fullWidth, id, type } = item;

    const content =
      type === "placement" ? (
        <LayoutSuspense>
          <PlacementFactory
            additionalProps={{ ...(additionalProps || {}), fullWidth }}
            content={inlineContent}
            id={id}
            shouldWait={true}
          />
        </LayoutSuspense>
      ) : (
        componentRenderers?.[id]?.()
      );

    return content && <>{content}</>;
  }

  function getListMatrixProps() {
    return {
      animate: false,
      center,
      horizontalSeparator,
      items: items?.map((item) => ({
        content: renderItem(item),
        type: item.type,
      })),
      numberOfColumns: numberOfColumns,
      rowClassName: styles.listRow,
      separator,
    };
  }

  function getBackgroundProps() {
    const { imagePath, type } = background;

    return {
      ...(type === "image" && { imageUrl: imagePath }),
      backgroundAttachment: "scroll",
    };
  }

  function getContentSectionStyle() {
    const { color, direction, minContentSectionHeight, theme, type } = background ?? {};
    const minHeight = childHeight < minContentSectionHeight ? minContentSectionHeight : childHeight;

    return {
      ...(type === "color" && { backgroundColor: color }),
      ...(type === "gradient" && {
        backgroundImage: getLinearGradient(theme, direction),
      }),
      minHeight: `${minHeight}px`,
    };
  }

  function getContainerStyle() {
    return settings?.fullHeightContent ? styles.containerWithoutPadding : styles.container;
  }

  function updateHeight(value) {
    setChildHeight(value[0].contentRect.height);
  }
};

ContentSection.displayName = "ContentSection";
ContentSection.propTypes = {
  /** Callback functions with custom registered components. */
  componentRenderers: PropTypes.object,

  /** Placement object for child placement ids and components. */
  placement: PropTypes.shape({
    /** Background image/color/none content for the section. */
    background: PropTypes.shape({
      color: PropTypes.string,
      direction: PropTypes.string,
      imagePath: PropTypes.string,
      minContentSectionHeight: PropTypes.number,
      overrides: PropTypes.object,
      theme: PropTypes.string,
      type: PropTypes.oneOf(["color", "gradient", "image", "none"]),
    }),
    contentItems: PropTypes.shape({
      items: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          type: PropTypes.string.isRequired,
        })
      ),
    }),
    heading: PropTypes.shape(placementHeadingPropTypes),
    id: PropTypes.string,
  }),

  /** Additional control over layout of placements and components. */
  settings: PropTypes.shape({
    center: PropTypes.bool,
    numberOfColumns: PropTypes.number.isRequired,
    overrides: PropTypes.object,
  }),
};
